// ─── Filter Shape Dispatcher (frontend) ──────────────────────────────────────
// Single source of truth for translating UI filter rows into partner-specific
// wire formats. Replaces the duplicated `if (isPAS) / else if (isST)` blocks
// previously scattered across FetchEngine, ReportAnalyzer, DiscrepancyMonitorV2.
//
// Filter shapes (must match values in partner_api_configs.filter_shape column):
//   - json-values-array          PAS: { key, values: [], operator }
//   - json-name-value-match      Criteo: { name, value: [], match }
//   - json-field-value-operator  Sharethrough: { field, value: [], operator, case_insensitive }
//   - query-string-only          Bidmachine: no filter object; filters go in URL params
//
// See backend/lib/filterShape.js for the backend mirror of this module.

// ─── Detect filter shape from request_body when filter_shape column is null ─
// Used for configs that haven't been backfilled with filter_shape yet.
// Mirrors today's sniffing logic in FetchEngine/DM/ReportAnalyzer.
export function detectFilterShape(requestBody = {}) {
  if (requestBody.hasOwnProperty('dimensions') || requestBody.hasOwnProperty('metrics')) {
    return 'json-values-array';                 // PAS-style
  }
  if (requestBody.hasOwnProperty('filterBy') || requestBody.hasOwnProperty('startDate')) {
    return 'json-field-value-operator';         // Sharethrough-style
  }
  // Default fallback: Criteo / generic shape
  return 'json-name-value-match';
}

// ─── Resolve filter shape from a config — explicit column wins, fallback to sniff ─
export function resolveFilterShape(config) {
  if (config?.filter_shape) return config.filter_shape;
  return detectFilterShape(config?.request_body || {});
}

// ─── Default operator for a new filter row, by shape ─────────────────────────
// Used when a user clicks "+ add filter" — what should the operator field
// pre-populate as?
export function defaultOperator(filter_shape) {
  switch (filter_shape) {
    case 'json-values-array':         return '=';
    case 'json-name-value-match':     return 'equals';
    case 'json-field-value-operator': return 'EQUALS';
    case 'query-string-only':         return '=';   // No filters in URL-only; placeholder for UI
    default:                          return 'equals';
  }
}

// ─── Whether the partner supports filters at all ─────────────────────────────
// Some partners (Bidmachine) don't support filtering. UI should hide filter UI.
export function supportsFilters(filter_shape) {
  return filter_shape !== 'query-string-only';
}

// ─── Build the filter array for outbound request body, given UI filter rows ──
// Returns the appropriately-shaped array OR null if the partner doesn't filter.
// Each input filter is `{key, value, operator}` from the UI.
//   - `value` is a comma-separated string from the input field
//   - `operator` is the user's chosen wire op (may be null → use default)
export function buildFilters(filter_shape, uiFilters) {
  if (!supportsFilters(filter_shape)) return null;
  if (!Array.isArray(uiFilters) || uiFilters.length === 0) return [];

  switch (filter_shape) {
    case 'json-values-array': {
      // PAS: { key, values: [...], operator }
      // CRITICAL: PAS always uses `values: [...]` (plural array) — never `value: ...` (scalar).
      // Sending scalar form returns [] with HTTP 200 (silent failure). Confirmed May 6, 2026.
      // Special case: multiple values + `=` operator → automatically becomes `IN`.
      const entries = [];
      uiFilters.forEach(f => {
        const vals = f.value ? f.value.split(',').map(v => v.trim()).filter(Boolean) : [];
        const op = f.operator === 'not equals' ? '!=' : (f.operator || '=');
        if (vals.length > 1 && op === '=') {
          entries.push({ key: f.key, values: vals, operator: 'IN' });
        } else if (vals.length > 1) {
          vals.forEach(v => entries.push({ key: f.key, values: [v], operator: op }));
        } else {
          entries.push({ key: f.key, values: [vals[0] || ''], operator: op });
        }
      });
      return entries;
    }

    case 'json-field-value-operator': {
      // Sharethrough: { field, value: [...], operator, case_insensitive }
      return uiFilters.map(f => ({
        field: f.key,
        value: f.value ? f.value.split(',').map(v => v.trim()).filter(Boolean) : [],
        operator: f.operator || 'EQUALS',
        case_insensitive: 1,
      }));
    }

    case 'json-name-value-match':
    default: {
      // Criteo / generic: { name, value: [...], match }
      return uiFilters.map(f => ({
        name: f.key,
        value: f.value ? f.value.split(',').map(v => v.trim()).filter(Boolean) : [],
        match: f.operator || 'equals',
      }));
    }
  }
}

// ─── Body keys for dimensions/metrics — different partners use different JSON keys ─
// PAS/Criteo use `dimensions`/`metrics` keys; Sharethrough uses `groupBy`/`fields`.
// Used by the dimension/metric picker UIs to know which body key to read/write.
export function getBodyKeys(filter_shape) {
  switch (filter_shape) {
    case 'json-field-value-operator':  // Sharethrough
      return { dimensions: 'groupBy', metrics: 'fields' };
    case 'query-string-only':           // Bidmachine — no body-based dims/metrics
      return { dimensions: null, metrics: null };
    case 'json-values-array':           // PAS
    case 'json-name-value-match':       // Criteo
    default:
      return { dimensions: 'dimensions', metrics: 'metrics' };
  }
}

// ─── Apply filters to a parsed request body, returning the updated body ──────
// Removes any pre-existing filter keys (`filters`, `filterBy`) before adding
// the new ones, so we never leave stale entries.
export function applyFiltersToBody(filter_shape, parsedBody, uiFilters) {
  const body = { ...parsedBody };
  delete body.filters;
  delete body.filterBy;

  if (!supportsFilters(filter_shape)) return body;
  if (!uiFilters || uiFilters.length === 0) return body;

  const entries = buildFilters(filter_shape, uiFilters);
  if (filter_shape === 'json-field-value-operator') {
    body.filterBy = entries;
  } else {
    body.filters = entries;
  }
  return body;
}
